/**
 * Per-crawler player-visibility, an OBR-presentation concern that deliberately
 * does NOT live on the DCC Character type (the rules core has no concept of
 * "visible to players" — that would pollute the vendored domain model and break
 * its byte-match with the desktop app's source).
 *
 * Instead the flag lives in a parallel `visibility` map in this player's DCC
 * metadata store, keyed by crawler id. Absent id = visible (the safe default:
 * a crawler is shown unless the GM has explicitly hidden it, and a brand-new
 * crawler is visible until toggled).
 *
 * This is Option A hiding: UI-level. The data still lives in room-readable
 * metadata; "hidden" means the roster does not render it, not that it is
 * cryptographically secret. For a trusted table that is the intended contract.
 */
import type { ObrPlayerApi } from '../obr/obrSdk.ts';
import { DCC_METADATA_KEY } from '../obr/obrStorage.ts';

/** The shape of the visibility sub-map inside the DCC store. */
type VisibilityMap = Record<string, boolean>;

function readVisibilityMap(meta: Record<string, unknown>): VisibilityMap {
  const raw = meta[DCC_METADATA_KEY];
  if (raw == null || typeof raw !== 'object') return {};
  const vis = (raw as { visibility?: unknown }).visibility;
  if (vis == null || typeof vis !== 'object') return {};
  return { ...(vis as VisibilityMap) };
}

/** True unless the GM has explicitly hidden this crawler. */
export function isVisibleToPlayers(map: VisibilityMap, crawlerId: string): boolean {
  return map[crawlerId] !== false;
}

export class VisibilityService {
  constructor(private player: ObrPlayerApi) {}

  /** The current visibility map for this player's crawlers. */
  async getMap(): Promise<VisibilityMap> {
    return readVisibilityMap(await this.player.getMetadata());
  }

  async isVisible(crawlerId: string): Promise<boolean> {
    return isVisibleToPlayers(await this.getMap(), crawlerId);
  }

  /**
   * Set a crawler's visibility. Read-modify-write of ONLY the visibility
   * sub-key, preserving the rest of the DCC store (crawlers, sessions, etc.)
   * and every sibling extension's metadata.
   */
  async setVisible(crawlerId: string, visible: boolean): Promise<void> {
    const meta = await this.player.getMetadata();
    const rawStore = meta[DCC_METADATA_KEY];
    const store = (rawStore != null && typeof rawStore === 'object')
      ? structuredClone(rawStore) as Record<string, unknown>
      : {};
    const visibility = (store.visibility != null && typeof store.visibility === 'object')
      ? store.visibility as VisibilityMap
      : {};
    visibility[crawlerId] = visible;
    store.visibility = visibility;
    await this.player.setMetadata({ [DCC_METADATA_KEY]: store });
  }

  /** Toggle and return the new value. */
  async toggle(crawlerId: string): Promise<boolean> {
    const next = !(await this.isVisible(crawlerId));
    await this.setVisible(crawlerId, next);
    return next;
  }
}
